import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  FiArrowLeft,
  FiHelpCircle,
  FiMail,
  FiUser,
  FiCalendar,
  FiEdit,
  FiUserCheck,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiFileText,
} from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import { fetchQuery } from '../../store/slices/adminSlice'
import { useDispatch, useSelector } from 'react-redux'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'
import Select from '../../components/Select/Select'
import FormInput from '../../components/FormInput/FormInput'
import './QueryDetail.css'

const QueryDetail = () => {
  const { queryId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { query: reduxQuery, isLoading: reduxLoading } = useSelector((state) => state.admin)
  // Handle response structure: backend returns { success, message, data: query }
  const query = reduxQuery?.data || reduxQuery

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [doubtSolvers, setDoubtSolvers] = useState([])
  const [activeTab, setActiveTab] = useState('overview')

  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [showFollowUpModal, setShowFollowUpModal] = useState(false)

  // Form states
  const [assignLoading, setAssignLoading] = useState(false)
  const [doubtSolverId, setDoubtSolverId] = useState('')
  const [statusLoading, setStatusLoading] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [resolveLoading, setResolveLoading] = useState(false)
  const [resolution, setResolution] = useState('')
  const [followUpLoading, setFollowUpLoading] = useState(false)
  const [followUpRequired, setFollowUpRequired] = useState(false)

  useEffect(() => {
    loadQuery()
    loadDoubtSolvers()
  }, [queryId])

  useEffect(() => {
    if (query) {
      setNewStatus(query.status || 'open')
      setDoubtSolverId(query.assignedTo?._id || query.assignedTo || '')
      setFollowUpRequired(query.followUpRequired || false)
      setResolution(query.resolution || '')
    }
  }, [query])

  const loadQuery = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await dispatch(fetchQuery(queryId)).unwrap()
      // Backend returns { success, message, data: query }
      // Redux thunk stores response.data, so result.data is the query
      if (!result?.data && !result?._id) {
        // Fallback: fetch directly if Redux doesn't have it
        const response = await adminApi.get(`/queries/${queryId}`)
        // Update Redux state manually if needed
        dispatch({ type: 'admin/fetchQuery/fulfilled', payload: response.data })
      }
    } catch (err) {
      setError(err?.message || err || 'Failed to load query')
    } finally {
      setLoading(false)
    }
  }

  const loadDoubtSolvers = async () => {
    try {
      const response = await adminApi.get('/users', {
        params: { role: 'doubt solver', limit: 1000 },
      })
      const usersList = response.data?.users || response.data?.data || []
      setDoubtSolvers(usersList)
    } catch (err) {
      console.error('Failed to load doubt solvers:', err)
    }
  }

  const handleAssign = async () => {
    if (!doubtSolverId) {
      setError('Please select a doubt solver')
      return
    }
    setAssignLoading(true)
    setError(null)
    try {
      await adminApi.patch(`/queries/${queryId}/assign`, {
        doubtSolverId,
      })
      await loadQuery()
      setShowAssignModal(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign query')
    } finally {
      setAssignLoading(false)
    }
  }

  const handleStartResolution = async () => {
    setStatusLoading(true)
    setError(null)
    try {
      await adminApi.patch(`/queries/${queryId}/start-resolution`)
      await loadQuery()
      setShowStatusModal(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start resolution')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleResolve = async () => {
    if (!resolution || resolution.trim().length < 10) {
      setError('Resolution must be at least 10 characters')
      return
    }
    setResolveLoading(true)
    setError(null)
    try {
      await adminApi.patch(`/queries/${queryId}/resolve`, {
        resolution,
      })
      await loadQuery()
      setShowResolveModal(false)
      setResolution('')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resolve query')
    } finally {
      setResolveLoading(false)
    }
  }

  const handleClose = async () => {
    setStatusLoading(true)
    setError(null)
    try {
      await adminApi.patch(`/queries/${queryId}/close`)
      await loadQuery()
      setShowStatusModal(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to close query')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleToggleFollowUp = async () => {
    setFollowUpLoading(true)
    setError(null)
    try {
      await adminApi.patch(`/queries/${queryId}/follow-up`, {
        followUpRequired: !followUpRequired,
      })
      await loadQuery()
      setShowFollowUpModal(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update follow-up status')
    } finally {
      setFollowUpLoading(false)
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'var(--color-error)'
      case 'high':
        return 'var(--color-warning)'
      case 'medium':
        return 'var(--color-primary)'
      case 'low':
        return 'var(--color-text-tertiary)'
      default:
        return 'var(--color-text-secondary)'
    }
  }

  const getStatusActions = () => {
    if (!query) return []
    const status = query.status
    const actions = []

    if (status === 'open') {
      actions.push({ label: 'Assign Query', action: () => setShowAssignModal(true) })
    }
    if (status === 'assigned') {
      actions.push({ label: 'Start Resolution', action: handleStartResolution })
    }
    if (['assigned', 'in-progress'].includes(status)) {
      actions.push({ label: 'Resolve Query', action: () => setShowResolveModal(true) })
    }
    if (status === 'resolved') {
      actions.push({ label: 'Close Query', action: handleClose })
    }

    return actions
  }

  if (loading || reduxLoading) {
    return <div className="detail-loading">Loading query details...</div>
  }

  if (error && !query) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate('/queries')}>Back to Queries</Button>
      </div>
    )
  }

  if (!query) {
    return (
      <div className="detail-error">
        <p>Query not found</p>
        <Button onClick={() => navigate('/queries')}>Back to Queries</Button>
      </div>
    )
  }

  return (
    <div className="query-detail-page">
      <header className="detail-header">
        <Link to="/queries" className="back-link">
          <FiArrowLeft size={20} />
          Back to Queries
        </Link>
        <div className="header-actions">
          {getStatusActions().map((action, idx) => (
            <Button
              key={idx}
              variant="secondary"
              onClick={action.action}
              disabled={statusLoading || assignLoading || resolveLoading}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </header>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="detail-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'resolution' ? 'active' : ''}
          onClick={() => setActiveTab('resolution')}
        >
          <FiFileText size={16} />
          Resolution
        </button>
        <button
          className={activeTab === 'context' ? 'active' : ''}
          onClick={() => setActiveTab('context')}
        >
          <FiUser size={16} />
          Student Context
        </button>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          {activeTab === 'overview' && (
            <>
              <section className="detail-card">
                <div className="card-header">
                  <div className="query-icon">
                    <FiHelpCircle size={32} />
                  </div>
                  <div>
                    <h1>{query.subject}</h1>
                    <div className="header-badges">
                      <StatusBadge status={query.status || 'open'} />
                      <span
                        className="priority-badge"
                        style={{
                          color: getPriorityColor(query.priority || 'medium'),
                        }}
                      >
                        {query.priority
                          ? query.priority.charAt(0).toUpperCase() +
                            query.priority.slice(1)
                          : 'Medium'}
                      </span>
                      <span className="query-type-badge">
                        {query.queryType || 'doubt'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <FiUser size={18} />
                    <div>
                      <span className="info-label">Student</span>
                      <strong>
                        {query.student?.firstName} {query.student?.lastName}
                      </strong>
                      {query.student?.email && (
                        <span className="info-sub">{query.student.email}</span>
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <FiCalendar size={18} />
                    <div>
                      <span className="info-label">Created</span>
                      <strong>
                        {query.createdAt
                          ? new Date(query.createdAt).toLocaleString()
                          : '—'}
                      </strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiUserCheck size={18} />
                    <div>
                      <span className="info-label">Assigned To</span>
                      {query.assignedTo ? (
                        <strong>
                          {query.assignedTo.firstName} {query.assignedTo.lastName}
                        </strong>
                      ) : (
                        <span className="text-muted">Unassigned</span>
                      )}
                    </div>
                  </div>
                  {query.resolvedAt && (
                    <div className="info-item">
                      <FiCheckCircle size={18} />
                      <div>
                        <span className="info-label">Resolved At</span>
                        <strong>
                          {new Date(query.resolvedAt).toLocaleString()}
                        </strong>
                      </div>
                    </div>
                  )}
                </div>

                <div className="description-section">
                  <h2>Description</h2>
                  <p className="description-text">{query.description}</p>
                </div>

                {query.attachments && query.attachments.length > 0 && (
                  <div className="attachments-section">
                    <h2>Attachments</h2>
                    <div className="attachments-list">
                      {query.attachments.map((att, idx) => (
                        <a
                          key={idx}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-link"
                        >
                          <FiFileText size={16} />
                          {att.originalName}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === 'resolution' && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Resolution</h2>
                {query.status === 'resolved' || query.status === 'closed' ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setResolution(query.resolution || '')
                      setShowResolveModal(true)
                    }}
                  >
                    <FiEdit size={18} />
                    Edit Resolution
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setResolution('')
                      setShowResolveModal(true)
                    }}
                  >
                    <FiCheckCircle size={18} />
                    Resolve Query
                  </Button>
                )}
              </div>
              {query.resolution ? (
                <div className="resolution-content">
                  <p>{query.resolution}</p>
                  {query.resolvedAt && (
                    <span className="resolution-date">
                      Resolved on:{' '}
                      {new Date(query.resolvedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>
                    This query has not been resolved yet. Click "Resolve Query" to
                    add a resolution.
                  </p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'context' && (
            <section className="detail-card">
              <h2>Student Context</h2>
              <div className="context-grid">
                <div className="context-item">
                  <h3>Batch Information</h3>
                  {query.batch ? (
                    <div>
                      <p>
                        <strong>Batch:</strong>{' '}
                        <Link to={`/batches/${query.batch._id || query.batch}`}>
                          {query.batch.batchName || 'Batch'}
                        </Link>
                      </p>
                      {query.batch.course && (
                        <p>
                          <strong>Course:</strong>{' '}
                          {typeof query.batch.course === 'object'
                            ? query.batch.course.title
                            : 'Course'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted">No batch information</p>
                  )}
                </div>

                {query.module && (
                  <div className="context-item">
                    <h3>Module</h3>
                    <p>
                      <strong>Module:</strong>{' '}
                      <Link to={`/modules/${query.module._id || query.module}`}>
                        {query.module.title || 'Module'}
                      </Link>
                    </p>
                    {query.module.order && (
                      <p>
                        <strong>Order:</strong> {query.module.order}
                      </p>
                    )}
                  </div>
                )}

                {query.lecture && (
                  <div className="context-item">
                    <h3>Lecture</h3>
                    <p>
                      <strong>Topic:</strong> {query.lecture.topic || 'Lecture'}
                    </p>
                    {query.lecture.startTime && (
                      <p>
                        <strong>Start Time:</strong>{' '}
                        {new Date(query.lecture.startTime).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <div className="context-item">
                  <h3>Query Metadata</h3>
                  <p>
                    <strong>Type:</strong> {query.queryType || 'doubt'}
                  </p>
                  <p>
                    <strong>Priority:</strong>{' '}
                    <span
                      style={{
                        color: getPriorityColor(query.priority || 'medium'),
                      }}
                    >
                      {query.priority
                        ? query.priority.charAt(0).toUpperCase() +
                          query.priority.slice(1)
                        : 'Medium'}
                    </span>
                  </p>
                  <p>
                    <strong>Status:</strong> <StatusBadge status={query.status} />
                  </p>
                  {query.followUpRequired && (
                    <p>
                      <strong>Follow-up Required:</strong>{' '}
                      <span className="follow-up-badge">Yes</span>
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              {!query.assignedTo && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setDoubtSolverId('')
                    setShowAssignModal(true)
                  }}
                >
                  <FiUserCheck size={16} />
                  Assign Query
                </Button>
              )}
              {query.assignedTo && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setDoubtSolverId(query.assignedTo?._id || query.assignedTo || '')
                    setShowAssignModal(true)
                  }}
                >
                  <FiUserCheck size={16} />
                  Reassign Query
                </Button>
              )}
              {query.status === 'assigned' && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handleStartResolution}
                  disabled={statusLoading}
                >
                  Start Resolution
                </Button>
              )}
              {['assigned', 'in-progress'].includes(query.status) && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setResolution('')
                    setShowResolveModal(true)
                  }}
                >
                  <FiCheckCircle size={16} />
                  Resolve Query
                </Button>
              )}
              {query.status === 'resolved' && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handleClose}
                  disabled={statusLoading}
                >
                  <FiXCircle size={16} />
                  Close Query
                </Button>
              )}
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowFollowUpModal(true)}
              >
                <FiClock size={16} />
                {query.followUpRequired ? 'Remove Follow-up' : 'Mark Follow-up'}
              </Button>
              {query.student?.email && (
                <a href={`mailto:${query.student.email}`}>
                  <Button variant="secondary" fullWidth>
                    <FiMail size={16} />
                    Email Student
                  </Button>
                </a>
              )}
            </div>
          </section>

          <section className="detail-card">
            <h3>Query Information</h3>
            <div className="info-list">
              <div className="info-row">
                <span className="info-label">Query ID</span>
                <span className="info-value">{query._id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Created</span>
                <span className="info-value">
                  {query.createdAt
                    ? new Date(query.createdAt).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Last Updated</span>
                <span className="info-value">
                  {query.updatedAt
                    ? new Date(query.updatedAt).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              {query.resolvedAt && (
                <div className="info-row">
                  <span className="info-label">Resolved</span>
                  <span className="info-value">
                    {new Date(query.resolvedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Assign Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false)
          setDoubtSolverId('')
        }}
        title="Assign Query to Doubt Solver"
        size="small"
      >
        <Select
          label="Select Doubt Solver"
          name="doubtSolverId"
          value={doubtSolverId}
          onChange={(e) => setDoubtSolverId(e.target.value)}
          options={[
            { value: '', label: 'Select a doubt solver' },
            ...doubtSolvers.map((s) => ({
              value: s._id,
              label: `${s.firstName} ${s.lastName}`.trim() || s.email,
            })),
          ]}
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowAssignModal(false)
              setDoubtSolverId('')
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAssign} loading={assignLoading}>
            Assign
          </Button>
        </div>
      </Modal>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => {
          setShowResolveModal(false)
          setResolution('')
        }}
        title="Resolve Query"
        size="medium"
      >
        <FormInput
          label="Resolution Details"
          name="resolution"
          type="textarea"
          rows={8}
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          placeholder="Enter detailed resolution for this query (minimum 10 characters)..."
          required
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowResolveModal(false)
              setResolution('')
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleResolve} loading={resolveLoading}>
            Resolve Query
          </Button>
        </div>
      </Modal>

      {/* Follow-up Modal */}
      <Modal
        isOpen={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        title={query.followUpRequired ? 'Remove Follow-up' : 'Mark Follow-up Required'}
        size="small"
      >
        <p>
          {query.followUpRequired
            ? 'Are you sure you want to remove the follow-up requirement for this query?'
            : 'Mark this query as requiring follow-up?'}
        </p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowFollowUpModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleToggleFollowUp} loading={followUpLoading}>
            {query.followUpRequired ? 'Remove Follow-up' : 'Mark Follow-up'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default QueryDetail

